import React from "react";
import "@/App.css";
import "@/index.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "@/contexts/AuthContext";
import { I18nProvider } from "@/contexts/I18nContext";
import { CompanyProvider } from "@/contexts/CompanyContext";
import { Toaster } from "@/components/ui/sonner";
import { hasAnyRole, userRoles } from "@/lib/api";

import Layout from "@/components/Layout";
import LoginPage from "@/pages/LoginPage";
import DashboardPage from "@/pages/DashboardPage";
import OrdersPage from "@/pages/OrdersPage";
import OrderDetailPage from "@/pages/OrderDetailPage";
import CalendarPage from "@/pages/CalendarPage";
import KitchenPage from "@/pages/KitchenPage";
import StaffPage from "@/pages/StaffPage";
import ReportsPage from "@/pages/ReportsPage";
import SettingsPage from "@/pages/SettingsPage";
import CustomerPortalPage from "@/pages/CustomerPortalPage";
import PublicTrackingPage from "@/pages/PublicTrackingPage";
import LogisticsPage from "@/pages/LogisticsPage";
import DeliveryDetailPage from "@/pages/DeliveryDetailPage";
import DriverDashboardPage from "@/pages/DriverDashboardPage";
import InventoryPage from "@/pages/InventoryPage";
import PurchasingPage from "@/pages/PurchasingPage";
import PurchaseOrderDetailPage from "@/pages/PurchaseOrderDetailPage";
import WarehousePage from "@/pages/WarehousePage";
import CostControlPage from "@/pages/CostControlPage";
import CommunicationsPage from "@/pages/CommunicationsPage";
import SignupRequestsPage from "@/pages/SignupRequestsPage";
import PermissionsPage from "@/pages/PermissionsPage";
import SignupPage from "@/pages/SignupPage";
import MenuPage from "@/pages/MenuPage";
import MarketingPage from "@/pages/MarketingPage";
import UsersPage from "@/pages/UsersPage";
import UtensilsPage from "@/pages/UtensilsPage";
import TasksPage from "@/pages/TasksPage";
import TaskManagerPage from "@/pages/TaskManagerPage";
import PlanEventPage from "@/pages/PlanEventPage";
import EnquiriesPage from "@/pages/EnquiriesPage";

function Protected({ children, roles }) {
  const { user, checking } = useAuth();
  if (checking) return <div className="min-h-screen flex items-center justify-center text-zinc-600">Loading…</div>;
  if (!user) return <Navigate to="/login" replace />;
  if (roles && !hasAnyRole(user, roles)) return <Navigate to="/dashboard" replace />;
  return children;
}

function RootRedirect() {
  const { user, checking } = useAuth();
  if (checking) return <div className="min-h-screen flex items-center justify-center text-zinc-600">Loading…</div>;
  if (!user) return <Navigate to="/login" replace />;
  const roles = userRoles(user);
  // Prioritize elevated roles over driver/customer-only redirects
  if (roles.includes("admin") || roles.includes("operations") || roles.includes("kitchen") || roles.includes("setup")) {
    return <Navigate to="/dashboard" replace />;
  }
  if (roles.includes("driver")) return <Navigate to="/driver" replace />;
  if (roles.includes("customer")) return <Navigate to="/portal" replace />;
  return <Navigate to="/dashboard" replace />;
}

export default function App() {
  return (
    <AuthProvider>
      <I18nProvider>
        <CompanyProvider>
          <BrowserRouter>
          <Toaster theme="dark" />
          <Routes>
            <Route path="/login" element={<LoginPage />} />
            <Route path="/signup" element={<SignupPage />} />
            <Route path="/track" element={<PublicTrackingPage />} />
            <Route path="/track/:code" element={<PublicTrackingPage />} />
            <Route path="/enquiry" element={<PlanEventPage standalone mode="normal" />} />
            <Route path="/customer-order" element={<PlanEventPage standalone mode="normal" />} />
            <Route path="/corporate-quotation" element={<PlanEventPage standalone mode="corporate" />} />

            <Route element={<Protected><Layout /></Protected>}>
              <Route path="/dashboard" element={<Protected roles={["admin", "operations", "kitchen", "driver", "setup"]}><DashboardPage /></Protected>} />
              <Route path="/orders" element={<OrdersPage />} />
              <Route path="/orders/:id" element={<OrderDetailPage />} />
              <Route path="/calendar" element={<CalendarPage />} />
              <Route path="/marketing" element={<Protected roles={["admin", "operations", "kitchen", "driver", "setup"]}><MarketingPage /></Protected>} />
              <Route path="/utensils" element={<Protected roles={["admin", "operations", "kitchen", "driver", "setup"]}><UtensilsPage /></Protected>} />
              <Route path="/tasks" element={<Protected roles={["admin", "operations", "kitchen", "driver", "setup"]}><TasksPage /></Protected>} />
              <Route path="/task-manager" element={<Protected roles={["admin", "operations"]}><TaskManagerPage /></Protected>} />
              <Route path="/kitchen" element={<Protected roles={["admin", "operations", "kitchen"]}><KitchenPage /></Protected>} />
              <Route path="/staff" element={<Protected roles={["admin", "operations"]}><StaffPage /></Protected>} />
              <Route path="/reports" element={<Protected roles={["admin", "operations"]}><ReportsPage /></Protected>} />
              <Route path="/settings" element={<SettingsPage />} />
              <Route path="/portal" element={<CustomerPortalPage />} />
              <Route path="/plan-event" element={<PlanEventPage mode="normal" />} />
              <Route path="/corporate" element={<PlanEventPage mode="corporate" />} />
              <Route path="/enquiries" element={<Protected roles={["admin", "operations"]}><EnquiriesPage /></Protected>} />

              <Route path="/logistics" element={<Protected roles={["admin", "operations"]}><LogisticsPage /></Protected>} />
              <Route path="/delivery/:id" element={<DeliveryDetailPage />} />
              <Route path="/driver" element={<DriverDashboardPage />} />
              <Route path="/inventory" element={<Protected roles={["admin", "operations", "kitchen", "driver", "setup"]}><InventoryPage /></Protected>} />
              <Route path="/menu" element={<Protected roles={["admin", "operations", "kitchen"]}><MenuPage /></Protected>} />
              <Route path="/purchasing" element={<Protected roles={["admin", "operations", "kitchen"]}><PurchasingPage /></Protected>} />
              <Route path="/purchasing/orders/:id" element={<Protected roles={["admin", "operations", "kitchen"]}><PurchaseOrderDetailPage /></Protected>} />
              <Route path="/warehouse" element={<Protected roles={["admin", "operations"]}><WarehousePage /></Protected>} />
              <Route path="/cost-control" element={<Protected roles={["admin", "operations"]}><CostControlPage /></Protected>} />
              <Route path="/communications" element={<CommunicationsPage />} />

              <Route path="/users" element={<Protected roles={["admin"]}><UsersPage /></Protected>} />
              <Route path="/signup-requests" element={<Protected roles={["admin", "operations"]}><SignupRequestsPage /></Protected>} />
              <Route path="/permissions" element={<Protected roles={["admin"]}><PermissionsPage /></Protected>} />
            </Route>

            <Route path="/" element={<RootRedirect />} />
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
          </BrowserRouter>
        </CompanyProvider>
      </I18nProvider>
    </AuthProvider>
  );
}
